/// <reference types="vite/client" />
// The reference is scoped to this file rather than added to the package's
// tsconfig `types`: only this module reads import.meta.env, and widening
// ImportMeta package-wide would let any file reach for it and typecheck
// cleanly on its way to a runtime undefined.

/**
 * The three build-time public settings, in one place.
 *
 * Every vertical is the same code with a different `.env`, and these are the
 * three values that carry that difference into the browser bundle. They are
 * inlined at BUILD time — the compose `args` become Dockerfile `ARG`/`ENV`
 * become `import.meta.env.VITE_*` in the bundle — which is why each vertical
 * builds its own image.
 *
 * # THIS FILE USED TO HAVE A TWIN, AND THE TWIN WAS A BUG
 *
 * While the Next→Vite conversion ran, the `process.env.NEXT_PUBLIC_*` reads
 * lived here and the `import.meta.env.VITE_*` reads lived in `config.vite.ts`,
 * and each app picked one with a `resolve.alias` on `@booking/core-ui/config`.
 * THAT ALIAS ONLY MATCHED THE PACKAGE SPECIFIER. The three importers inside
 * this package — `auth/oauth-core.ts`, `liff/company.ts` and
 * `liff/pages/AccountPage.tsx` — import `"../config"` relatively, so they kept
 * resolving to the Next file, where Vite substitutes `process.env` with `{}`.
 * AUTH_SERVICE_URL and OAUTH_CLIENT_ID were empty strings in every shipped
 * bundle, so `getAuthorizeUrl()` built a same-origin `/authorize?client_id=`
 * and login could not reach the auth service at all.
 *
 * `vite-router-plugin.ts` exists because of exactly this failure and matches on
 * the RESOLVED FILE instead; the router was swapped correctly and the config
 * was not. Nothing caught it: the realstack suites inject tokens through the
 * auth double rather than driving a real OAuth redirect, so all 99 specs passed
 * against a build whose login was broken. `scripts/check-bundles.sh` is the
 * guard added afterwards.
 *
 * There is one file again because Next is gone. Do not reintroduce a second one.
 *
 * # WHAT DOES NOT BELONG HERE, AND WHY
 *
 * `ANALYTICS_TRACKER_ID`, `ERROR_TRACKER_KEY_ADMIN`, `ERROR_TRACKER_KEY_LIFF`
 * and `APP_ENV` are templated into the HTML at REQUEST time by the Go SPA
 * server and read from `window.__RUNTIME_CONFIG__` (see `runtime-config.ts`).
 * Rotating one of those keys is a container recreate, not a rebuild. Moving
 * them here would freeze them into six images — the exact thing that design
 * forbids. They stay where they are.
 *
 * # WHY THESE ARE EAGER
 *
 * Read at module scope, not behind a getter. Two callers already do that
 * (`auth/oauth-core.ts`, `liff/pages/AccountPage.tsx` build a URL at import
 * time), and a bundler only inlines `import.meta.env.X` where it can see it
 * literally — so a lazy read is both a behaviour change and a value that may
 * not be substituted at all.
 */

import type { RuntimeConfig } from "./runtime-config";

/*
  REQUEST-TIME FIRST (teisyovision plan.md phase 10): the three values come from the
  request-time runtime config first -- the site server templates them per host,
  so one build serves both branches -- and the build-time VITE_ values only
  when it is absent (`vite dev`, the e2e preview). Still eager: the server
  writes the runtime config before any module runs.
*/
const runtime: RuntimeConfig = (typeof globalThis !== "undefined" && globalThis.__RUNTIME_CONFIG__) || {};

/** The auth service origin. Empty when unset, which callers treat as "no auth service configured". */
export const AUTH_SERVICE_URL: string = runtime.authServiceUrl || import.meta.env.VITE_AUTH_SERVICE_URL || "";

/** The OAuth client id for this vertical. */
export const OAUTH_CLIENT_ID: string = runtime.oauthClientId || (import.meta.env.VITE_OAUTH_CLIENT_ID ?? "");

/**
 * This vertical's company id.
 *
 * Falls back to 1 — the canonical first company — only when the env is absent,
 * which is local dev without a `.env`. Each vertical has its own database, so
 * every one of them really is company 1 there.
 */
export const COMPANY_ID: number = runtime.companyId || Number(import.meta.env.VITE_COMPANY_ID) || 1;
